import axios from "axios";
import { Dictionary } from "../types/utils.types";
import {
  DEFAULT_REDIRECTION_STATES,
  FALLBACK_MESSAGE,
  NETWORK_ERROR_MESSAGES,
  OFFLINE_MESSAGE,
  UNEXPECTED_STATUS_MESSAGE,
  NO_RESPONSE_MESSAGE,
} from "../constants/APIErrorMessages";
import { IRedirectionState } from "../types/apiErrorHandler.types";
import { HTTP_STATUS_CODES } from "../constants/HttpConstants";

function apiErrorHandler(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  matchStatuses: number[],
  fallBackErrorMessage: string,
  skipRedirect = false,
  redirectionStates: Dictionary<IRedirectionState> = {},
): string | null {
  if (!navigator.onLine) {
    return OFFLINE_MESSAGE;
  }
  try {
    if (axios.isAxiosError(error)) {
      const { response, code: axiosErrorCode } = error;
      if (response) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { status, data } = response;
        const extendedRedirectionState = Object.assign(
          DEFAULT_REDIRECTION_STATES,
          redirectionStates,
        );
        const redirectionState = extendedRedirectionState[status];
        let next;

        // Handle network errors based on Axios error code
        if (
          axiosErrorCode &&
          // eslint-disable-next-line no-prototype-builtins
          NETWORK_ERROR_MESSAGES.hasOwnProperty(axiosErrorCode)
        ) {
          const networkErrorMessage = NETWORK_ERROR_MESSAGES[axiosErrorCode];
          return networkErrorMessage.message;
        }

        // Check if the response status matches any specified status codes
        if (matchStatuses.includes(response.status)) {
          if (
            [HTTP_STATUS_CODES.UNAUTHORIZED].includes(status) &&
            redirectionState.canContainNext
          ) {
            next = `?next=${window.location.href}`;
          }

          if (redirectionState?.redirect && !skipRedirect) {
            window.location.replace(`${redirectionState.url}${next ?? ""}`);
            return null;
          }

          if (
            [
              HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR,
              HTTP_STATUS_CODES.BAD_GATEWAY,
              HTTP_STATUS_CODES.SERVICE_UNAVAILABLE,
              HTTP_STATUS_CODES.GATEWAY_TIMEOUT,
            ].includes(status) &&
            redirectionState.message
          ) {
            return redirectionState.message;
          }
          // Check if the response contains a custom error message
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (data?.message) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
            return data.message;
          }
        } else {
          // Handle unmatched status codes
          throw new Error(
            UNEXPECTED_STATUS_MESSAGE({
              status,
            }),
          );
        }
      } else {
        throw new Error(NO_RESPONSE_MESSAGE);
      }
    } else {
      // Handle non-Axios errors (e.g., network issues)
      throw error;
    }
  } catch (unhandledError) {
    return fallBackErrorMessage || FALLBACK_MESSAGE;
  }
  return null;
}

export default apiErrorHandler;
